import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  Children,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  FaFolder,
  FaListAlt,
  FaFileAlt,
  FaCheck,
  FaTimes,
  FaPlus,
  FaBars,
  FaEllipsisV,
} from "react-icons/fa";
import { FiRefreshCw, FiLogOut } from "react-icons/fi";
import { motion } from "framer-motion";
import {
  addFolder,
  setActiveFolder,
  getFoldersFromLocalStorage,
  setFiles,
  setFolderOrderIds,
  setFileOrderIds,
  deleteFolderOrTodo,
  loadFilesFromLocalStorage,
} from "../redux/folder/folderSlice";
import { fileType, isMacOS } from "../common/common";
import {
  create_folder_thunk,
  create_or_update_file_order,
  create_or_update_folder_order,
  delete_folder_thunk,
  move_file_thunk,
  update_file_color_thunk,
} from "../redux/folder/folderThunk";
import { logout_thunk, sync_data_thunk } from "../redux/auth/authThunk";
import ContextMenu from "./Sidebar/ContextMenu";
import { setErrorAlert } from "../redux/alert/alertSlice";

// Define drag types

export default function Sidebar({
  openTodo,
  activeTab,
  setActiveTab,
  changeBackgroundColor,
  updateTodoInOpenTabs,
  deleteTodoFromOpenTabs,
  sidebarInputRef,
  todoInputRef,
  todoEditInputRef,
  tabRenameInputRef,
  setShortCutFileRef,
  activeTabData,
  setActiveTabData,
}) {
  const token = localStorage.getItem("todoToken");
  const dispatch = useDispatch();
  const folders = useSelector((state) => state.folder.folders);
  const files = useSelector((state) => state.folder.files);
  const activeFolder = useSelector((state) => state.folder.activeFolder);
  const folderLoading = useSelector((state) => state.folder.loading);
  // const selectedFolder = useSelector((state) => state.folder.selectedFolder);
  // const selectedFile = useSelector((state) => state.folder.selectedFile);
  const todoLoading = useSelector((state) => state.todo.loading);
  const openTabLoading = useSelector((state) => state.opentab.loading);
  const shortcutsKeys = useSelector((state) => state.shortcut.shortcuts);
  const user = useSelector((state) => state.auth.user);
  const folderOrderIds = useSelector((state) => state.folder.folderOrderIds);
  const fileOrderIds = useSelector((state) => state.folder.fileOrderIds);

  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [addingType, setAddingType] = useState(null);
  const [newItemName, setNewItemName] = useState("");
  const [expandedFolders, setExpandedFolders] = useState({});
  const [contextMenu, setContextMenu] = useState(null);
  const [renameId, setRenameId] = useState(null);
  const [renameText, setRenameText] = useState("");
  const sidebarRef = useRef(null);
  const [focusedItemIndex, setFocusedItemIndex] = useState(null);
  const [flattenedItems, setFlattenedItems] = useState([]);
  const [focusedFilesIndex, setFocusedFilesIndex] = useState(null);
  const [flattenedFiles, setFlattenedFiles] = useState([]);
  const [isFloderFocus, setIsFloderFocus] = useState(true);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [filteredFolders, setFilteredFolders] = useState([]);
  const [sidebarWidth, setSidebarWidth] = useState(
    window.innerWidth < 640
      ? 321
      : parseInt(localStorage.getItem("leftWidth"), 10) || 401
  );
  const [isResizing, setIsResizing] = React.useState(false);
  const [isResizingSidebar, setIsResizingSidebar] = React.useState(false);
  const [leftWidth, setLeftWidth] = React.useState(
    window.innerWidth < 640
      ? 160
      : parseInt(localStorage.getItem("leftWidth"), 10) || 200
  );
  const [lastFileFocusIndexInFolder, setLastFocusFileIndexInFolder] = useState(
    {}
  );
  const containerRef = React.useRef(null);
  const [isDragging, setIsDragging] = useState(true);
  const [hexValue, setHexValue] = useState("#1F293B");
  const contextMenuRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const scrollContainerFileRef = useRef(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [lastClickTime, setLastClickTime] = useState({ id: null, time: 0 });

  const handleSync = async () => {
    dispatch(sync_data_thunk({ token }));
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    dispatch(getFoldersFromLocalStorage());
    dispatch(setActiveFolder("default-folder"));
    setSelectedFolder("default-folder");
    dispatch(setFiles(null));
  }, []);

  useEffect(() => {
    if (activeFolder === "default-folder") {
      dispatch(setFiles(null));
    }
  }, [folders]);

  useEffect(() => {
    if (activeTab) {
      setSelectedFile(activeTab);
      if (activeTabData?.parentId) {
        setSelectedFolder(activeTabData?.parentId);
        dispatch(setActiveFolder(activeTabData?.parentId));
        dispatch(setFiles(activeTabData?.parentId));
      } else {
        setSelectedFolder("default-folder");
        dispatch(setActiveFolder("default-folder"));
        dispatch(setFiles(null));
      }
    }
  }, [activeTab]);

  const flattenStructure = (items = folders) => {
    let flat =
      (items?.length > 0 &&
        items?.filter((item) => item.type === fileType[0])) ||
      [];

    // Ensure the "Default" folder is always included
    const defaultFolder =
      flat?.length > 0 && flat.find((item) => item.name === "Default");

    if (!defaultFolder) {
      flat.unshift({
        id: "default-folder",
        name: "Default",
        type: 0,
      });
    }

    return flat;
  };

  useEffect(() => {
    const flat = flattenStructure();
    setFlattenedItems(flat);
  }, [folders, expandedFolders]);
  useEffect(() => {
    const flat = files;
    setFlattenedFiles(flat);
  }, [files]);

  useEffect(() => {
    if (selectedFolder) {
      // setFocusedItemIndex(null);
      const index =
        flattenedItems?.length > 0 &&
        flattenedItems.findIndex((item) => item.id === selectedFolder);
      setFocusedItemIndex(index !== -1 ? index : null); // Update focused index or set to null
      if (
        lastFileFocusIndexInFolder[selectedFolder] ||
        lastFileFocusIndexInFolder[selectedFolder] === 0
      ) {
        setFocusedFilesIndex(lastFileFocusIndexInFolder[selectedFolder]);
      }
    } else {
      setFocusedItemIndex(null); // Reset if no folder is selected
    }
  }, [selectedFolder, flattenedItems]);
  useEffect(() => {
    if (selectedFile) {
      // setFocusedItemIndex(null);
      const index = flattenedFiles?.findIndex(
        (item) => item.id === selectedFile
      );
      setFocusedFilesIndex(index !== -1 ? index : null); // Update focused index or set to null
      setLastFocusFileIndexInFolder((prev) => ({
        ...prev,
        [selectedFolder]: index,
      }));
    } else {
      setFocusedFilesIndex(null); // Reset if no folder is selected
    }
  }, [selectedFile, flattenedFiles]);
  // Update flattened items when folders or expanded state changes

  useEffect(() => {
    if (focusedItemIndex !== null && flattenedItems[focusedItemIndex]) {
      const focusedElement = document.getElementById(
        `item-${flattenedItems[focusedItemIndex].id}`
      );
      const container = scrollContainerRef.current;

      if (focusedElement && container) {
        // Get element position
        const elementRect = focusedElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Calculate distances from top and bottom of container
        const elementTop = elementRect.top - containerRect.top;
        const elementBottom = elementRect.bottom - containerRect.top;
        const containerHeight = container.clientHeight;

        // Check if element is outside visible area
        if (elementTop < 0 || elementBottom > containerHeight) {
          // Calculate new scroll position that keeps context
          let newScrollTop;

          if (elementTop < 0) {
            // Scrolling up: Position element 1/4 down from top
            newScrollTop =
              container.scrollTop + elementTop - containerHeight * 0.25;
          } else {
            // Scrolling down: Position element 3/4 down from top
            newScrollTop =
              container.scrollTop + (elementBottom - containerHeight * 0.75);
          }

          // Smooth scroll to new position
          container.scrollTo({
            top: newScrollTop,
            behavior: "auto",
          });
        }
      }
    }
  }, [focusedItemIndex, flattenedItems, activeTab]);

  useEffect(() => {
    if (focusedFilesIndex !== null && flattenedFiles[focusedFilesIndex]) {
      const focusedElement = document.getElementById(
        `item-file-${flattenedFiles[focusedFilesIndex].id}`
      );
      const container = scrollContainerFileRef.current;

      if (focusedElement && container) {
        // Get element position
        const elementRect = focusedElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Calculate distances from top and bottom of container

        const elementTop = elementRect.top - containerRect.top;
        const elementBottom = elementRect.bottom - containerRect.top;
        const containerHeight = container.clientHeight;

        // Check if element is outside visible area
        if (elementTop < 0 || elementBottom > containerHeight) {
          // Calculate new scroll position that keeps context
          let newScrollTop;

          if (elementTop < 0) {
            // Scrolling up: Position element 1/4 down from top
            newScrollTop =
              container.scrollTop + elementTop - containerHeight * 0.25;
          } else {
            // Scrolling down: Position element 3/4 down from top
            newScrollTop =
              container.scrollTop + (elementBottom - containerHeight * 0.75);
          }

          // Smooth scroll to new position
          container.scrollTo({
            top: newScrollTop,
            behavior: "auto",
          });
        }
      }
    }
  }, [focusedFilesIndex, flattenedFiles, activeTab]);
  // Add keyboard navigation handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      console.log("e", e);
      if (
        document.activeElement === sidebarInputRef?.current ||
        document.activeElement === todoInputRef?.current ||
        document.activeElement === todoEditInputRef?.current ||
        document.activeElement === tabRenameInputRef?.current ||
        document.activeElement === setShortCutFileRef?.current
      ) {
        // Let the input's default Ctrl+A etc behavior work
        if (
          (e.metaKey &&
            ["a", "c", "v", "z", "x"].includes(e.key.toLowerCase())) ||
          e.key === "Backspace" ||
          e.key === "Delete" ||
          e.key === "ArrowLeft" ||
          e.key === "ArrowRight"
        ) {
          return; // Allow default behavior for these shortcuts
        }
      }

      if (
        e.key === "Enter" &&
        !renameId &&
        (focusedItemIndex !== null || focusedFilesIndex !== null) &&
        !addingType
      ) {
        e.preventDefault();
        if (isFloderFocus) {
          const focusedItem = flattenedItems[focusedItemIndex];
          if (focusedItem) {
            handleFolderClick(focusedItem.id);
          }
          return;
        } else {
          const focusedFiles = flattenedFiles[focusedFilesIndex];
          if (focusedFiles) {
            setSelectedFile(focusedFiles.id);
            openTodo(focusedFiles);
            setActiveTab(focusedFiles.id);
            setActiveTabData(focusedFiles);
          }
          return;
        }
      }
      // Existing keyboard shortcuts

      if (["Control", "Alt", "Shift", "Meta", "Enter"].includes(e.key)) {
        return;
      }

      // Construct the new key combination
      const modifiers = [];
      if (e.ctrlKey) modifiers.push("Ctrl");
      if (e.altKey) {
        let isMac = isMacOS();
        if (isMac) {
          modifiers.push("Option");
        } else {
          modifiers.push("Alt");
        }
      }
      if (e.shiftKey) modifiers.push("Shift");
      if (e.metaKey) modifiers.push("Cmd");

      // Handle special keys
      let key = e.key;
      if (key === " ") key = "Space";

      // Ensure original character is used, not the special/alternate character
      if (e.altKey) {
        // Use the base key without converting to uppercase
        key = e.code.replace("Key", "");
      } else if (key.length === 1) {
        key = key.toUpperCase();
      }

      const newKey = [...modifiers, key].join("+");
      let shortcutId;
      shortcutsKeys?.Sidebar.forEach((shortcut) => {
        if (shortcut.key === newKey) {
          shortcutId = shortcut.id;
        }
      });
      switch (shortcutId) {
        case "sidebar-add-todo":
          e.preventDefault();
          setAddingType(fileType[1]);
          setNewItemName("");
          return;
        case "sidebar-add-folder":
          e.preventDefault();
          setAddingType(fileType[0]);
          setNewItemName("");
          return;
        case "sidebar-nav-up":
          e.preventDefault();
          if (isFloderFocus) {
            setFocusedItemIndex((prev) =>
              prev > 0 ? prev - 1 : flattenedItems.length - 1
            );
            let upIndex =
              focusedItemIndex > 0
                ? focusedItemIndex - 1
                : flattenedItems.length - 1;
            handleFolderClick(flattenedItems[upIndex].id);
          } else {
            setFocusedFilesIndex((prev) =>
              prev > 0 ? prev - 1 : flattenedFiles.length - 1
            );
            let upIndex =
              focusedFilesIndex > 0
                ? focusedFilesIndex - 1
                : flattenedFiles.length - 1;
            handleFileClick(flattenedFiles[upIndex]);
          }
          return;
        case "sidebar-nav-down":
          e.preventDefault();
          if (isFloderFocus) {
            setFocusedItemIndex((prev) =>
              prev < flattenedItems.length - 1 ? prev + 1 : 0
            );
            let downIndex =
              focusedItemIndex < flattenedItems.length - 1
                ? focusedItemIndex + 1
                : 0;
            handleFolderClick(flattenedItems[downIndex].id);
          } else {
            setFocusedFilesIndex((prev) =>
              prev < flattenedFiles.length - 1 ? prev + 1 : 0
            );
            let downIndex =
              focusedFilesIndex < flattenedFiles.length - 1
                ? focusedFilesIndex + 1
                : 0;
            handleFileClick(flattenedFiles[downIndex]);
          }
          return;
        case "sidebar-input-close":
          e.preventDefault();
          setAddingType(null);
          return;
        case "sidebar-nav-left":
          e.preventDefault();
          setIsFloderFocus(true);
          // setFocusedFilesIndex(null);
          return;
        case "sidebar-nav-right":
          e.preventDefault();
          setIsFloderFocus(false);

          if (
            lastFileFocusIndexInFolder[selectedFolder] &&
            lastFileFocusIndexInFolder[selectedFolder] !== null &&
            lastFileFocusIndexInFolder[selectedFolder] !== -1
          ) {
            handleFileClick(
              flattenedFiles[lastFileFocusIndexInFolder[selectedFolder]]
            );
            setFocusedFilesIndex(lastFileFocusIndexInFolder[selectedFolder]);
          } else {
            if (flattenedFiles[0]) {
              handleFileClick(flattenedFiles[0]);
              setFocusedFilesIndex(0);
            }
          }

          return;
        case "sidebar-sync":
          e.preventDefault();
          handleSync();
          return;
        default:
          break;
        // return;
      }

      if (
        e.ctrlKey &&
        (/^[a-zA-Z]$/.test(e.key) || /^Key[A-Z]$/.test(e.code))
      ) {
        e.preventDefault();
        const shortcutKey = e.key.toLowerCase();
        setIsFloderFocus(false);

        // Find the folder with the matching shortcut
        const filesToOpen =
          files?.length > 0 &&
          files.find((folder) => folder.shortcut === shortcutKey);

        if (filesToOpen) {
          setSelectedFile(filesToOpen.id); // Set the folder as selected
          openTodo(filesToOpen); // Open the folder/todo
          setActiveTab(filesToOpen.id);
          setActiveTabData(filesToOpen);
        }
      }
      if (e.ctrlKey && /^[0-9]$/.test(e.key)) {
        e.preventDefault();
        const shortcutKey = e.key.toLowerCase();
        setIsFloderFocus(true);

        if (shortcutKey === user?.defaultFolderShortcut) {
          handleFolderClick("default-folder");
          return;
        }
        // Find the folder with the matching shortcut
        const folderToOpen =
          folders?.length > 0 &&
          folders.find((folder) => folder.shortcut === shortcutKey);

        if (folderToOpen) {
          // setSelectedFile(filesToOpen.id); // Set the folder as selected
          // openTodo(filesToOpen); // Open the folder/todo
          // setActiveTab(filesToOpen.id);
          // setActiveTabData(filesToOpen);
          handleFolderClick(folderToOpen.id);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    flattenedItems,
    focusedItemIndex,
    flattenedFiles,
    focusedFilesIndex,
    renameId,
    addingType,
    isFloderFocus,
    shortcutsKeys,
    selectedFile,
    selectedFolder,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the sidebar is clicked outside
      if (sidebarRef.current && !sidebarRef.current?.contains(event.target)) {
        setAddingType(null);
        setNewItemName("");

        // Check if the context menu is open and click is outside it
      }
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target)
      ) {
        // Only close context menu if the color picker is not open
        if (
          !showColorPicker ||
          (contextMenuRef.current &&
            !contextMenuRef.current.contains(event.target))
        ) {
          closeContextMenu();
          setRenameId(null);
          setRenameText("");
        }
      }

      // If the color picker is open, we don't want the context menu to close when clicked inside the picker.
      if (showColorPicker && !event.target.closest(".color-picker-wrapper")) {
        let data = {
          id: contextMenu?.item?.id,
          backgroundColor: hexValue,
        };
        dispatch(update_file_color_thunk({ data, token }));
        changeBackgroundColor(data, true);
        setShowColorPicker(false); // Close color picker if clicked outside
      }
    };

    // Attach the event listener to the document
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup the event listener when the component is unmounted or context changes
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [contextMenu, showColorPicker, hexValue]); // Add contextMenu as dependency to keep track of it

  useEffect(() => {
    const updatedFilteredFolders = folders.filter((item) => item.type === 0);
    const defaultFolder =
      updatedFilteredFolders?.length > 0 &&
      updatedFilteredFolders.find((item) => item.name === "Default");

    if (!defaultFolder) {
      updatedFilteredFolders.unshift({
        id: "default-folder",
        name: "Default",
        type: 0,
      });
    }

    setFilteredFolders(updatedFilteredFolders);
  }, [folders]);

  const startResizing = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
  }, []);

  // Sidebar width resize logic
  const startResizingSidebar = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizingSidebar(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      // Handle internal divider resize
      if (isResizing && containerRef.current) {
        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();
        const newLeftWidth = e.clientX - containerRect.left;

        if (newLeftWidth >= 100 && newLeftWidth <= containerRect.width - 100) {
          setLeftWidth(newLeftWidth);
          localStorage.setItem("leftWidth", newLeftWidth);
        }
      }

      // Handle sidebar width resize
      if (isResizingSidebar && sidebarRef.current) {
        const sidebarRect = sidebarRef.current.getBoundingClientRect();
        const newWidth = Math.max(256, Math.min(800, e.clientX));
        setSidebarWidth(newWidth);
        // localStorage.setItem("sidebarWidth", newWidth);
      }
    };

    const stopResizing = () => {
      setIsResizing(false);
      setIsResizingSidebar(false);
    };

    if (isResizing || isResizingSidebar) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", stopResizing);
      // Prevent text selection while resizing
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", stopResizing);
      document.body.style.userSelect = "";
    };
  }, [isResizing, isResizingSidebar]);

  let clickTimeout = null;

  const handleItemClick = (event, item, type) => {
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      clickTimeout = null;
      handleRightClick(event, item); // Double-click triggers context menu
    } else {
      clickTimeout = setTimeout(() => {
        clickTimeout = null;
        if (type === "folder") {
          handleFolderClick(item.id);
        } else if (type === "file") {
          handleFileClick(item);
          setIsMobileMenuOpen(false);
        }
      }, 500); // Double-click threshold
    }
  };

  const handleRightClick = (event, item) => {
    event.preventDefault();
    event.stopPropagation();

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const menuWidth = 200; // Approximate width of context menu
    const menuHeight = 300; // Approximate height of context menu

    let x = event.clientX;
    let y = event.clientY;

    const spaceBelow = screenHeight - y;
    const spaceAbove = y;

    const shouldOpenUpward = spaceBelow < menuHeight && spaceAbove > spaceBelow;

    // If the screen width is small, center the menu relative to the clicked item
    if (screenWidth < 600) {
      x = Math.max(10, event.clientX - menuWidth / 2);
    }

    setHexValue(item?.backgroundColor);
    setAddingType(null);
    setNewItemName("");

    if (item.type === fileType[0] && !expandedFolders[item.id]) {
      setExpandedFolders((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
    }

    setContextMenu({
      x,
      y,
      item,
      openUpward: shouldOpenUpward,
    });
  };

  const closeContextMenu = () => setContextMenu(null);

  const handleFolderClick = (folderId) => {
    setIsFloderFocus(true);
    // Restore the previous focusedFilesIndex for the folder
    if (focusedFilesIndex || focusedFilesIndex === 0) {
      setLastFocusFileIndexInFolder((prev) => ({
        ...prev,
        [selectedFolder]: focusedFilesIndex,
      }));
    }

    setFocusedFilesIndex(null);

    setSelectedFolder(folderId);
    dispatch(setActiveFolder(folderId));

    if (folderId === "default-folder") {
      dispatch(setFiles(null));
    } else {
      dispatch(setFiles(folderId));
    }

    setAddingType(null);
  };

  const handleFileClick = (fileProp) => {
    setIsFloderFocus(false);
    setSelectedFile(fileProp.id);
    openTodo(fileProp);
    setActiveTab(fileProp.id);
    setActiveTabData(fileProp);
    setIsMobileMenuOpen(false);
  };

  const FolderItem = ({ item, index }) => {
    const [{ isDragging }, drag] = useDrag({
      type: ItemTypes.FOLDER,
      item: { id: item.id, index, type: ItemTypes.FOLDER },
      canDrag: item.id !== "default-folder",
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const [{ isOver }, drop] = useDrop({
      accept: [ItemTypes.FOLDER, ItemTypes.FILE],
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
      drop: (droppedItem, monitor) => {
        // Prevent handling if already handled by a nested target
        if (monitor.didDrop()) {
          return;
        }

        if (droppedItem.type === ItemTypes.FILE) {
          handleFileMove(droppedItem.index, 0, item.id);
          return { dropped: true, folderId: item.id };
        }
      },
      hover: (draggedItem, monitor) => {
        if (draggedItem.type === ItemTypes.FOLDER) {
          const dragIndex = draggedItem.index;
          const hoverIndex = index;

          if (dragIndex === hoverIndex) return;
          handleFolderMove(dragIndex, hoverIndex);
          draggedItem.index = hoverIndex;
        }
      },
    });

    const ref = (el) => {
      drag(el);
      drop(el);
    };

    const isFocused =
      focusedItemIndex !== null &&
      flattenedItems &&
      flattenedItems[focusedItemIndex]?.id === item.id;
    const isSelected = focusedItemIndex !== null && selectedFolder === item.id;

    return (
      <motion.li
        ref={ref}
        id={`item-${item.id}`}
        className={`group relative rounded-lg transition-all duration-200 cursor-pointer py-[4px] px-[2px]
            ${
              isSelected
                ? "bg-gray-800/75 shadow-lg ring-2 ring-blue-500/50"
                : "hover:bg-gray-700/50"
            }
            ${isFocused ? "ring-2 ring-blue-500/50" : ""}
            ${isDragging ? "opacity-50" : ""}`}
        style={{
          backgroundColor: isSelected
            ? isFloderFocus
              ? "rgba(130, 180, 250, 0.2)"
              : "rgb(31 41 55 / 0.75)"
            : "rgb(17 24 39 / 0.5)",
          opacity: isDragging ? 0.5 : 1,
        }}
        onContextMenu={(event) => handleRightClick(event, item)}
      >
        <div
          className="flex  items-center px-3 py-2 gap-2 min-w-0"
          onClick={() => handleFolderClick(item.id)}
        >
          <FaFolder className="text-yellow-500 w-4 h-4" />
          <span className="truncate">{item.name}</span>

          {window.innerWidth > 640 && item.shortcut && (
            <span className="ml-auto text-xs text-gray-400">
              Ctrl+{item.shortcut}
            </span>
          )}
          <div
            className="ml-auto w-fit md:hidden flex cursor-pointer"
            onClick={(event) => {
              event.stopPropagation();
              handleRightClick(event, item);
            }}
          >
            <FaEllipsisV className="text-gray-400 w-fit hover:text-white" />
          </div>
        </div>
        {isOver && (
          <div className="absolute inset-0 bg-blue-500/20 ring-2 ring-blue-500/50" />
        )}
      </motion.li>
    );
  };

  const FileItem = ({ item, index }) => {
    const [{ isDragging }, drag] = useDrag({
      type: ItemTypes.FILE,
      item: {
        id: item.id,
        index,
        type: ItemTypes.FILE,
        parentId: item.parentId,
        folderId: selectedFolder,
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const [, drop] = useDrop({
      accept: ItemTypes.FILE,
      hover: (draggedItem, monitor) => {
        if (!monitor.isOver({ shallow: true })) return;

        const dragIndex = draggedItem.index;
        const hoverIndex = index;

        if (dragIndex === hoverIndex) return;

        handleFileMove(dragIndex, hoverIndex);
        draggedItem.index = hoverIndex;
      },
    });

    const ref = (el) => {
      drag(el);
      drop(el);
    };

    const isFocused =
      flattenedFiles && flattenedFiles[focusedFilesIndex]?.id === item.id;

    return (
      <motion.div
        ref={ref}
        id={`item-file-${item.id}`}
        className={`group relative rounded-lg transition-all duration-200 cursor-pointer py-[4px] px-[2px]
            ${
              selectedFile === item.id
                ? "shadow-lg ring-2 ring-blue-500/50"
                : "hover:bg-gray-700/50"
            }
            ${isFocused ? "ring-2 ring-blue-500/50" : ""}
            ${isDragging ? "shadow-lg" : ""}`}
        style={{
          backgroundColor:
            selectedFile === item.id
              ? item?.backgroundColor ??
                (isFloderFocus
                  ? "rgb(31 41 55 / 0.75)"
                  : "rgba(130, 180, 250, 0.2)")
              : item?.backgroundColor ?? "rgb(17 24 39 / 0.5)",
          opacity: isDragging ? 0.5 : 1,
          zIndex: isDragging ? 9999 : "auto",
        }}
        onContextMenu={(event) => handleRightClick(event, item)}
      >
        <div
          className="flex items-center px-4 py-1 gap-2 min-w-0"
          onClick={() => handleFileClick(item)}
        >
          <FaListAlt className="text-blue-400 w-4 h-4" />
          <span className="truncate">{item.name}</span>

          {window.innerWidth > 640 && item.shortcut && (
            <span className="ml-auto text-xs text-gray-400">
              Ctrl+{item.shortcut}
            </span>
          )}
          {window.innerWidth > 640 &&
            item.id === "default-folder" &&
            user?.defaultFolderShortcut && (
              <span className="ml-auto text-xs text-gray-400">
                Ctrl+{user?.defaultFolderShortcut}
              </span>
            )}
          <div
            className="ml-auto w-fit md:hidden flex cursor-pointer"
            onClick={(event) => {
              event.stopPropagation();
              handleRightClick(event, item);
            }}
          >
            <FaEllipsisV className="text-gray-400 w-fit hover:text-white" />
          </div>
        </div>
      </motion.div>
    );
  };

  const handleAddButtonClick = (type) => {
    setAddingType(type);
    setNewItemName("");
    setContextMenu(null); // Close context menu
  };

  const handleAddItem = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (!newItemName.trim()) return;

    try {
      // Clear form state immediately
      const itemName = newItemName.trim();
      setAddingType(null);
      setNewItemName("");

      const newItem = {
        id: uuidv4(),
        name: itemName,
        parentId:
          addingType === fileType[1]
            ? selectedFolder === "default-folder"
              ? null
              : selectedFolder
            : null,
        type: addingType,
        createdAt: new Date().toISOString(),
      };

      // Handle orders
      let newFolderOrder;
      let newFileOrder;

      if (addingType === fileType[0]) {
        // newFolderOrder = [...folderOrderIds];
        // newFolderOrder.splice(1, 0, newItem.id);
        // newItem.folderOrder = newFolderOrder;
      } else if (addingType === fileType[1]) {
        // const folderId = newItem.parentId || "default-folder";
        // let backendFolderId = newItem.parentId;
        // // let fileOrderIds =
        // const currentFileOrder =
        //   fileOrderIds[folderId] ||
        //   files
        //     .filter((file) => file.parentId === folderId)
        //     .map((file) => file.id);
        // let currentFileOrder;
        // if (
        //   fileOrderIds[folderId] ===
        //   files.filter((file) => file.parentId === backendFolderId)?.length
        // ) {
        //   currentFileOrder =
        //     fileOrderIds[folderId] ||
        //     files
        //       .filter((file) => file.parentId === backendFolderId)
        //       .map((file) => file.id);
        // } else {
        //   currentFileOrder = files
        //     .filter((file) => file.parentId === backendFolderId)
        //     .map((file) => file.id);
        // }
        // newFileOrder = [newItem.id, ...currentFileOrder];
        // newItem.folderOrder = newFileOrder;
      }

      // Batch related state updates
      if (addingType === fileType[1]) {
        const folderId = newItem.parentId || "default-folder";
        dispatch(addFolder({ data: newItem }));
        // dispatch(
        //   setFileOrderIds({
        //     folderId,
        //     fileIds: newFileOrder,
        //   })
        // );
        setFocusedFilesIndex(-1);
        dispatch(
          setFiles(activeFolder === "default-folder" ? null : newItem.parentId)
        );

        // Update UI state
        openTodo(newItem);
        setActiveTab(newItem.id);
        setActiveTabData(newItem);
        setIsFloderFocus(false);
        setSelectedFile(newItem.id);
      } else {
        dispatch(addFolder({ data: newItem }));
        // dispatch(setFolderOrderIds(newFolderOrder));
        setFocusedItemIndex(-1);
        dispatch(setFiles(newItem.id));

        // Update UI state
        setIsFloderFocus(true);
        setSelectedFolder(newItem.id);
        dispatch(setActiveFolder(newItem.id));
      }

      // Make API call last
      dispatch(create_folder_thunk({ data: newItem, token }));
    } catch (error) {
      console.error("Error creating item:", error);
      // Add error handling/user notification here
    }
  };

  const renderAddFolderForm = () => {
    if (addingType === fileType[0]) {
      return (
        <form onSubmit={handleAddItem} className="mb-2 ">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center">
              <FaFolder className="w-4 h-4 text-yellow-500/75" />
            </div>
            <input
              type="text"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              // placeholder={``}
              ref={sidebarInputRef}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleAddItem(e); // Call the function on Enter
                }
              }}
              className={`w-full pl-10 pr-12 py-2 rounded-lg
                  bg-gray-800/75
                  hover:bg-gray-800 
                  text-gray-100 placeholder-gray-400
                  border hover:border-gray-600
                  transition-all duration-200
                 outline-none ring-2 
                ring-yellow-500/50 border-yellow-500/50
                `}
              autoFocus
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
              <button
                type="submit"
                className={`p-1.5 rounded-md transition-colors  text-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/10 outline-none`}
              >
                <FaCheck className="w-3 h-3" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setAddingType(null);
                  setNewItemName("");
                }}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-300 hover:bg-gray-700/50 transition-colors"
              >
                <FaTimes className="w-3 h-3" />
              </button>
            </div>
          </div>
        </form>
      );
    }
    return null;
  };
  const renderAddFilesForm = () => {
    if (addingType === fileType[1]) {
      return (
        <motion.form onSubmit={handleAddItem} className="mb-2 ">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center">
              <FaListAlt className="w-4 h-4 text-blue-400/75" />
            </div>
            <input
              type="text"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              ref={sidebarInputRef}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleAddItem(e); // Call the function on Enter
                }
              }}
              className={`w-full pl-10 pr-12 py-2 rounded-lg
                  bg-gray-800/75
                  hover:bg-gray-800 
                  text-gray-100 placeholder-gray-400
                  border  hover:border-gray-600
                  transition-all duration-200
                  outline-none ring-2 
                  ring-blue-500/50 border-blue-500/50
                  `}
              autoFocus
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
              <button
                type="submit"
                className={`p-1.5 rounded-md transition-colors text-blue-500 hover:text-blue-400 hover:bg-blue-500/10`}
              >
                <FaCheck className="w-3 h-3" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setAddingType(null);
                  setNewItemName("");
                }}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-300 hover:bg-gray-700/50 transition-colors"
              >
                <FaTimes className="w-3 h-3" />
              </button>
            </div>
          </div>
        </motion.form>
      );
    }
    return null;
  };

  const SectionHeaderFolder = ({ title, onAdd, icon: Icon }) => (
    <div className="flex items-center justify-between px-3 py-2 text-gray-400 bg-gray-900 sticky top-0 z-10">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium text-gray-400">{title}</span>
      </div>
      <button
        onClick={onAdd}
        className="p-1 hover:bg-gray-700/50 rounded-md hover:border-yellow-500/50 hover:text-yellow-500 bg-transparent border border-gray-500/50"
      >
        <FaPlus className="w-3 h-3" />
      </button>
    </div>
  );
  const SectionHeaderFiles = ({ title, onAdd, icon: Icon }) => (
    <div className="flex items-center justify-between px-3 py-2 text-gray-400 bg-gray-900 sticky top-0 z-10">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium text-gray-400">{title}</span>
      </div>
      <button
        onClick={onAdd}
        className="p-1 hover:bg-gray-700/50 rounded-md hover:border-blue-500/50 hover:text-blue-500 bg-transparent border border-gray-500/50"
      >
        <FaPlus className="w-3 h-3" />
      </button>
    </div>
  );

  // const FolderStructure = () => {

  //   return (

  //   );
  // };

  const ItemTypes = {
    FOLDER: "folder",
    FILE: "file",
  };

  const handleFolderMove = async (dragIndex, hoverIndex) => {
    try {
      const newFolders = [...filteredFolders];
      const sourceFolder = newFolders[dragIndex];
      const destinationFolder = newFolders[hoverIndex];

      if (
        sourceFolder.id === "default-folder" ||
        destinationFolder?.id === "default-folder"
      ) {
        dispatch(setErrorAlert("You cannot move the default folder"));
        return;
      }

      const [movedFolder] = newFolders.splice(dragIndex, 1);
      newFolders.splice(hoverIndex, 0, movedFolder);
      const newFolderIds = newFolders.map((folder) => folder.id);

      setFilteredFolders(newFolders);
      dispatch(setFolderOrderIds(newFolderIds));

      const flat = flattenStructure(newFolders);
      setFlattenedItems(flat);

      dispatch(
        create_or_update_folder_order({
          data: { folderOrder: newFolderIds },
          token,
        })
      );
    } catch (error) {
      dispatch(
        setErrorAlert("Failed to update folder order. Please try again.")
      );
    }
  };

  const handleFileMove = (dragIndex, hoverIndex, targetFolderId = null) => {
    try {
      if (!targetFolderId || targetFolderId === selectedFolder) {
        // Handle reordering within the same folder
        const fileIds = files.map((file) => file.id);
        const [removed] = fileIds.splice(dragIndex, 1);
        fileIds.splice(hoverIndex, 0, removed);

        dispatch(setFileOrderIds({ fileIds, folderId: selectedFolder }));
        const parentId = files[0]?.parentId;
        dispatch(setFiles(parentId));

        dispatch(
          create_or_update_file_order({
            data: { fileOrder: fileIds, folderId: selectedFolder },
            token,
          })
        );
      } else {
        // Handle moving file to a different folder
        const file = files[dragIndex];

        // Create a copy of the file in the new folder

        handleDeleteFileFromFolder({
          id: file.id,
          parentId: file.parentId,
          type: fileType[1],
        });
        handleAddItemWithFolder(
          { preventDefault: () => {} },
          targetFolderId,
          file
        );

        // Delete the file from the original folder

        // Update UI to show the new folder's contents
        dispatch(setActiveFolder(targetFolderId || "default-folder"));
        setSelectedFolder(targetFolderId || "default-folder");
        dispatch(setFiles(targetFolderId));
      }
    } catch (error) {
      console.error("Error moving file:", error);
      dispatch(setErrorAlert("Failed to move file. Please try again."));
    }
  };

  // const FilesContainer = ({
  //   files,
  //   scrollContainerFileRef,
  //   renderAddFilesForm,
  // }) => {
  //   const [, drop] = useDrop({
  //     accept: ItemTypes.FILE,
  //     drop: (item, monitor) => {
  //       const didDrop = monitor.didDrop();
  //       if (didDrop) return;

  //       const dragIndex = item.index;
  //       const hoverIndex = files.length;

  //       if (dragIndex !== hoverIndex) {
  //         handleFileMove(dragIndex, hoverIndex);
  //       }
  //     },
  //   });

  //   return (
  //     <div
  //       className="flex-1 overflow-y-auto fancy-scrollbar"
  //       ref={(el) => {
  //         drop(el);
  //         if (scrollContainerFileRef) {
  //           scrollContainerFileRef.current = el;
  //         }
  //       }}
  //     >
  //       <div className="space-y-1 p-2">
  //         {renderAddFilesForm()}
  //         <ul className="list-none space-y-1">
  //           {files?.length > 0 &&
  //             files?.map((item, index) => (
  //               <FileItem key={item.id} item={item} index={index} />
  //             ))}
  //         </ul>
  //       </div>
  //     </div>
  //   );
  // };

  // New helper function that takes the target folder as a parameter
  const handleAddItemWithFolder = async (e, targetFolder, item) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (!item.name.trim()) return;

    try {
      setAddingType(null);
      setNewItemName("");

      const newItem = {
        id: item?.id,
        backgroundColor: item?.backgroundColor,
        children: item?.children,
        name: item.name.trim(),
        parentId: targetFolder === "default-folder" ? null : targetFolder,
        todos: item?.todos,
        userId: item?.userId,
        type: fileType[1], // Assuming we're always adding a file
        createdAt: new Date().toISOString(),
        shortcut: item?.shortcut,
      };

      dispatch(setFiles(newItem?.parentId));
      // Handle file order
      const folderId = newItem.parentId || "default-folder";
      let data = {
        parentId: newItem?.parentId,
      };
      let filesData = loadFilesFromLocalStorage(data);
      let fileOrderIdData = filesData.map((file) => file.id);
      const currentFileOrder = fileOrderIds[folderId] || fileOrderIdData;
      const newFileOrder = [newItem.id, ...currentFileOrder];
      newItem.folderOrder = newFileOrder;

      // Update state
      dispatch(
        setFileOrderIds({
          folderId,
          fileIds: newFileOrder,
        })
      );
      dispatch(addFolder({ data: newItem }));
      setFocusedFilesIndex(-1);
      dispatch(setFiles(newItem?.parentId));

      // // Update UI state
      // openTodo(newItem);
      // setActiveTab(newItem.id);
      // setActiveTabData(newItem);
      // setIsFloderFocus(false);
      // setSelectedFile(newItem.id);

      // Make API call
      let apiData = {
        fileId: item?.id,
        sourceFolderId: item?.parentId,
        targetFolderId: newItem?.parentId,
      };
      handleFileClick(newItem);

      dispatch(move_file_thunk({ data: apiData, token }));
    } catch (error) {
      console.error("Error creating item:", error);
    }
  };

  const handleDeleteFileFromFolder = (data) => {
    deleteTodoFromOpenTabs(data.id);
    dispatch(deleteFolderOrTodo({ data }));
    if (data.type === fileType[1]) {
      dispatch(setFiles(data.parentId));
      const folderId = data.parentId || "default-folder";
      const filterFileOrderIds =
        fileOrderIds[folderId]?.filter((id) => id !== data.id) || [];
      dispatch(
        setFileOrderIds({
          folderId,
          fileIds: filterFileOrderIds,
        })
      );
    } else if (data.type === fileType[0]) {
      const filterFolderOrderIds =
        folderOrderIds?.filter((id) => id !== data.id) || [];
      dispatch(setFolderOrderIds(filterFolderOrderIds));
    }
    // dispatch(delete_folder_thunk({ id: data.id, token }));
    closeContextMenu();
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <>
        <div className="md:hidden flex justify-between items-center bg-gray-900 text-white p-4">
          <h2 className="text-lg font-bold">To-do List</h2>
          <button
            className="md:hidden z-50 p-2 bg-gray-800 text-white outline-none ring-0 border-none rounded-md"
            onClick={toggleMobileMenu}
          >
            <FaBars className="w-4 h-4" />
          </button>
        </div>

        <div
          className={`fixed inset-y-0 left-0 transform ${
            isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          } md:relative h-full flex-1 md:translate-x-0 transition duration-200 ease-in-out z-30 md:z-auto`}
        >
          <div
            ref={sidebarRef}
            className="flex flex-col bg-gray-900 text-white shadow-lg border-r border-gray-600 h-full relative"
            style={{
              width: `${sidebarWidth}px`,
              minWidth: "256px",
              overflowX: "hidden",
            }}
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-800 bg-gray-900">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold">To-do List</h2>
              </div>
            </div>

            {/* Main content area */}
            <div className="flex-1 overflow-hidden">
              <div className="flex h-full relative" ref={containerRef}>
                {/* Folders section */}
                <div
                  className="h-full transition-all duration-200"
                  style={{ width: `${leftWidth}px` }}
                >
                  <div className="h-full">
                    <div className="flex flex-col h-full">
                      <SectionHeaderFolder
                        title="Folders"
                        onAdd={() => handleAddButtonClick(fileType[0])}
                        icon={FaFolder}
                      />
                      <div
                        className="flex-1 overflow-y-auto fancy-scrollbar"
                        ref={scrollContainerRef}
                      >
                        <div className="space-y-1 p-2">
                          {renderAddFolderForm(null)}
                          <ul className="list-none space-y-1 folder-list-container">
                            {filteredFolders?.length > 0 &&
                              filteredFolders.map((item, index) => (
                                <FolderItem
                                  key={item.id}
                                  item={item}
                                  index={index}
                                />
                              ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Files section */}
                <div
                  className="h-full"
                  style={{ width: `calc(100% - ${leftWidth}px)` }}
                >
                  <div className="h-full">
                    <div className="flex flex-col h-full">
                      <SectionHeaderFiles
                        title="Files"
                        onAdd={() => handleAddButtonClick(fileType[1])}
                        icon={FaFileAlt}
                      />
                      <div
                        className="flex-1 overflow-y-auto fancy-scrollbar"
                        ref={
                          scrollContainerFileRef
                          // drop(el);
                          // if (scrollContainerFileRef) {
                          //   scrollContainerFileRef.current = el;
                          // }
                        }
                      >
                        <div className="space-y-1 p-2">
                          {renderAddFilesForm()}
                          <ul className="list-none space-y-1">
                            {files?.length > 0 &&
                              files.map((item, index) => (
                                <FileItem
                                  key={item.id}
                                  item={item}
                                  index={index}
                                />
                              ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Internal resizable divider */}
                <div
                  className={`absolute h-full cursor-col-resize bg-transparent z-10
              hover:after:bg-blue-500/50 after:content-[''] after:absolute 
              after:left-1/2 after:top-0 after:bottom-0 after:w-0.5 
              after:bg-gray-700/50 after:-translate-x-1/2
              ${isResizing ? "after:bg-blue-500" : ""}`}
                  style={{ left: `${leftWidth}px` }}
                  onMouseDown={startResizing}
                >
                  <div
                    className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 
                w-4 h-8 flex items-center justify-center opacity-0 
                hover:opacity-100 transition-opacity`}
                  >
                    <div className="w-0.5 h-4 bg-blue-400" />
                  </div>
                </div>
              </div>
              {/* Sidebar resize handle */}
              <div
                className={`absolute right-0 top-0 bottom-0 cursor-col-resize z-50
            hover:bg-blue-500/50 group
            ${isResizingSidebar ? "bg-blue-500/75" : "bg-transparent"}`}
                onMouseDown={startResizingSidebar}
              >
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100"></div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-3 bg-gradient-to-t from-gray-900 via-gray-900 to-gray-900/95 border-t border-gray-600/50">
              <div className="flex justify-evenly items-center gap-2">
                {/* Sync button */}
                <div className="relative group">
                  <button
                    onClick={handleSync}
                    disabled={todoLoading || folderLoading || openTabLoading}
                    className="flex items-center justify-center gap-2 bg-gray-800/95 hover:bg-gray-700/95 
                shadow-md text-white rounded-lg px-3 py-2 outline-none 
                transition-all duration-200 ease-in-out disabled:opacity-50
                hover:shadow-lg hover:shadow-gray-900/50"
                  >
                    <FiRefreshCw
                      className={`w-4 h-4 ${
                        todoLoading || folderLoading || openTabLoading
                          ? "animate-spin"
                          : ""
                      }`}
                    />
                  </button>
                </div>

                {/* Logout button */}
                <div className="relative group">
                  <button
                    onClick={() => dispatch(logout_thunk())}
                    className="flex items-center justify-center gap-2 bg-gray-800/95 hover:bg-gray-700/95 
                shadow-md text-white rounded-lg px-3 py-2 outline-none 
                transition-all duration-200 ease-in-out
                hover:shadow-lg hover:shadow-gray-900/50"
                  >
                    <FiLogOut className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
            {/* Context Menu */}
            {contextMenu && (
              <ContextMenu
                renameText={renameText}
                setRenameText={setRenameText}
                renameId={renameId}
                setRenameId={setRenameId}
                token={token}
                fileType={fileType}
                updateTodoInOpenTabs={updateTodoInOpenTabs}
                deleteTodoFromOpenTabs={deleteTodoFromOpenTabs}
                changeBackgroundColor={changeBackgroundColor}
                contextMenu={contextMenu}
                closeContextMenu={closeContextMenu}
                contextMenuRef={contextMenuRef}
                hexValue={hexValue}
                setHexValue={setHexValue}
                isDragging={isDragging}
                setIsDragging={setIsDragging}
                showColorPicker={showColorPicker}
                setShowColorPicker={setShowColorPicker}
                tabRenameInputRef={tabRenameInputRef}
                setShortCutFileRef={setShortCutFileRef}
                files={files}
                folders={folders}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              />
            )}

            {/* Sidebar resize handle */}
            <div
              className={`absolute right-0 top-0 bottom-0 w-0.5 cursor-col-resize z-50
          hover:bg-blue-500/50 group`}
              onMouseDown={(e) => {
                e.preventDefault();
                const startX = e.pageX;
                const startWidth = sidebarWidth;

                const doDrag = (e) => {
                  let newWidth = Math.max(
                    256,
                    Math.min(800, startWidth + e.pageX - startX)
                  );
                  setSidebarWidth(newWidth);
                  localStorage.setItem("sidebarWidth", newWidth);
                };

                const stopDrag = () => {
                  document.removeEventListener("mousemove", doDrag);
                  document.removeEventListener("mouseup", stopDrag);
                };

                document.addEventListener("mousemove", doDrag);
                document.addEventListener("mouseup", stopDrag);
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="h-16 bg-blue-400/75 rounded-full mx-auto" />
              </div>
            </div>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
            onClick={toggleMobileMenu}
          ></div>
        )}
      </>
    </DndProvider>
  );
}
